import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useGlossaryBlueprintsData } from "../../../../lib/useTemplatesData";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useGlossaryDataContext } from "../../../../providers/ObjectsDataProvider";
import { getSelectedGame } from "@/lib/stores/unifiedUserStore";
import { fetchTags } from "../../../../lib/stores/tagsStore";
import { useToast } from "../../../../hooks/use-toast";
import { useConfirmation } from "../../../../hooks/use-confirmation";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { getAvailableViewModesForGame } from "@/lib/utils/platformMapping";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import { useMigrationPreview } from "@/hooks/useMigrationPreview";
import { parseGlossaryItemData } from "@/lib/utils/objectParser";
import { useItemLock } from "@/hooks/useItemLock";
import { useItemUpdate } from "@/hooks/useItemUpdate";
import { useWizardDraft, ObjectDraftData } from "@/hooks/useWizardDraft";

// Local imports
import { GlossaryWizardProps, WizardMode, ViewDisplayMode } from "./types";
import { generateDefaultObjectName } from "./utils/objectNameGenerator";
import { useBlueprintVersionManagement } from "./hooks/useBlueprintVersionManagement";
import { useCodePreviewGenerator } from "./hooks/useCodePreviewGenerator";
import { useObjectWizardHandlers } from "./hooks/useObjectWizardHandlers";
import { ObjectWizardContent } from "./components/ObjectWizardContent";

// Shared components
import { useGlossaryState } from "../useObjectState";
import { UnifiedWizardHeader } from "../../shared/UnifiedWizardHeader";
import { UnsavedChangesDialog } from "@/components/ui/UnsavedChangesDialog";
import { DraftBadge } from "@/components/ui/DraftBadge";
import { LockStatusBanner } from "@/components/ui/LockStatusBanner";
import { UpdateNotificationBanner } from "@/components/ui/UpdateNotificationBanner";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const GlossaryWizard = ({
  isOpen = false,
  onClose,
  initialData,
  onSave,
  onUpdate,
  inline = false,
  onUnsavedChangesChange,
  onRefreshData,
  onLockReleaseReady,
}: GlossaryWizardProps) => {
  const { t } = usePlatformTranslation();

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [wizardMode, setWizardMode] = useState<WizardMode>(
    initialData ? "edit" : "create"
  );
  const [viewDisplayMode, setViewDisplayMode] =
    useState<ViewDisplayMode>("structured");
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<"close" | "cancel" | null>(
    null
  );

  // ============================================================================
  // HOOKS & DATA
  // ============================================================================

  const gameId = getSelectedGame();
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const { currentUser } = useSimplifiedUserData();

  // Get available view modes for this game
  const availableViewModes = getAvailableViewModesForGame(gameId);

  // Check if user can edit based on owner and permissions (without lock consideration)
  const canEditByPermission = useMemo(() => {
    if (!initialData) return true; // Can always edit new items
    // If no owner, anyone can edit
    if (!initialData.owner) return true;
    // If user is owner, can edit
    if (initialData.owner === currentUser?.id) return true;
    // If locked and user is not owner, cannot edit
    if (initialData.edit_permissions === 'Locked') return false;
    // If open, anyone can edit
    return true;
  }, [initialData, currentUser?.id]);

  // Item lock system for collaborative editing
  const {
    lockStatus,
    canEdit: canEditByLock,
    isAcquiring: isAcquiringLock,
    lockError,
    isLockedByOther,
    acquireLock,
    releaseLock,
    refreshLockStatus,
    forceLock,
    releaseImmediately,
  } = useItemLock({
    itemId: initialData?.id,
    itemType: 'object',
    gameId: gameId?.id,
    autoAcquire: true,
    isEditMode: wizardMode === 'edit',
    // onLockFailed is not needed - LockStatusBanner shows the lock status
    onLockLost: () => {
      // This toast IS needed - user should know immediately when they lose the lock
      toast({
        title: t('lock.lockLost', 'Edit lock lost'),
        description: t('lock.lockLostDescription', 'Another user has taken over editing. Your changes may not be saved.'),
        variant: 'destructive',
      });
      // Switch to view mode when lock is lost
      setWizardMode('view' as WizardMode);
    },
    onLockBecameAvailable: () => {
      // Lock was released by another user - refresh data to get their changes
      console.log('[GlossaryWizard] Lock became available, refreshing data');
      onRefreshData?.();
      toast({
        title: t('lock.lockAvailable', 'Edit access available'),
        description: t('lock.lockAvailableDescription', 'You now have edit access. Data has been refreshed.'),
      });
      // Switch back to edit mode
      setWizardMode('edit' as WizardMode);
    },
  });

  // Combined edit permission: must have both permission AND lock
  const canEdit = canEditByPermission && canEditByLock;

  // Expose lock release function to parent (for explicit tab close)
  useEffect(() => {
    if (onLockReleaseReady && releaseImmediately) {
      onLockReleaseReady(releaseImmediately);
    }
  }, [onLockReleaseReady, releaseImmediately]);

  // Item update detection for collaborative editing
  const {
    hasPendingUpdate,
    updatedByName,
    updatedByAvatar,
    updatedAt,
    clearPendingUpdate,
  } = useItemUpdate({
    itemId: initialData?.id,
    itemType: 'object',
    gameId: gameId?.id,
    enabled: true,
    onItemUpdated: (updatedBy, action) => {
      // Only show toast if user is actively viewing (not editing)
      if (wizardMode === 'view') {
        toast({
          title: t('update.dataUpdated', 'Data updated'),
          description: t('update.refreshPrompt', '{{name}} updated this item. Refresh to see changes.', { name: updatedBy }),
        });
      }
    },
  });

  // Glossary blueprints data
  const {
    getCachedData,
    isLoading,
  } = useGlossaryBlueprintsData({
    gameId: gameId?.id,
  });

  // Glossary items for counting untitled items
  const { glossaryItems } = useGlossaryDataContext();

  // Get available blueprints for the current game
  const glossaryBlueprints = gameId ? getCachedData() : [];

  // Get the blueprint for the initial data if editing
  const currentBlueprint = useMemo(() => {
    if (initialData?.blueprint_id) {
      return (
        glossaryBlueprints.find((bp) => bp.id === initialData.blueprint_id) ||
        null
      );
    }
    return null;
  }, [initialData?.blueprint_id, glossaryBlueprints]);

  // Draft persistence for tab switching
  const {
    draftData,
    hasDraftData,
    saveDraftData,
    clearDraft,
    saveImmediately,
  } = useWizardDraft<ObjectDraftData>({
    draftType: 'object',
    gameId: gameId?.id,
    itemId: initialData?.id,
    enabled: wizardMode === 'create' || wizardMode === 'edit',
  });

  // Custom hook for managing glossary state
  const glossaryState = useGlossaryState({
    initialData,
    blueprint: currentBlueprint,
    draftData,
  });

  // Track when glossaryState is initialized to avoid false change detection
  const hasInitializedRef = React.useRef(false);
  
  // Mark state as initialized after first render cycle completes
  React.useEffect(() => {
    // Defer to next tick to ensure useGlossaryState has initialized parsedSections
    setTimeout(() => {
      hasInitializedRef.current = true;
    }, 0);
  }, []);

  // Reset initialization flag when switching objects
  React.useEffect(() => {
    hasInitializedRef.current = false;
    // Re-enable after next tick
    setTimeout(() => {
      hasInitializedRef.current = true;
    }, 0);
  }, [initialData?.id]);

  // Normalize section data for consistent comparison
  // Strips out properties that shouldn't affect change detection
  const normalizeSectionsForComparison = useCallback((sections: any[]) => {
    if (!Array.isArray(sections)) return sections;
    
    return sections.map(section => ({
      id: section.id,
      title: section.title,
      fields: (section.fields || []).map((field: any) => ({
        id: field.id,
        type: field.type,
        label: field.label,
        value: field.value,
        required: field.required || false,
        multiple: field.multiple || false,
        translation: field.translation || false,
        filter: field.filter || false,
        string_id: field.string_id,
        // Explicitly exclude metadata properties that don't affect actual data:
        // - documentation, hiddenForRoles, readOnlyForRoles, filterOptions, isReadOnly
        // - These get added during parseGlossaryItemData from blueprint metadata
        //   but shouldn't trigger change detection since they're not user edits
      }))
    }));
  }, []);

  // Track unsaved changes
  // Transform initialData to match the format of getCurrentData()
  // We need to parse initialData.data the same way glossaryState does
  const initialDataForComparison = useMemo(() => {
    if (!initialData) return {};
    
    // Parse the data to get enriched sections with default fields
    // This matches what glossaryState.parsedSections will have
    let parsedSections = initialData.data || [];
    if (initialData && currentBlueprint) {
      try {
        const { sections } = parseGlossaryItemData(initialData, currentBlueprint);
        parsedSections = sections;
      } catch (error) {
        console.warn('[ObjectWizard] Failed to parse initial data:', error);
      }
    }
    
    return {
      name: initialData.name,
      tags: initialData.tags || [],
      identifiers: initialData.identifiers || [],
      sections: normalizeSectionsForComparison(parsedSections),
      blueprintId: initialData.blueprint_id || '',
    };
  }, [initialData, currentBlueprint, normalizeSectionsForComparison]);

  const unsavedChanges = useUnsavedChanges(
    initialDataForComparison,
    () => ({
      name: glossaryState.itemTitle,
      tags: glossaryState.itemTags,
      identifiers: glossaryState.itemIdentifiers,
      sections: normalizeSectionsForComparison(glossaryState.parsedSections),
      blueprintId: glossaryState.selectedBlueprintId,
    }),
    wizardMode === "edit" || wizardMode === "create"
  );

  // Blueprint version management
  const {
    blueprintChanges,
    currentItemBlueprintVersion,
    setCurrentItemBlueprintVersion,
    setBlueprintChanges,
  } = useBlueprintVersionManagement({
    initialData,
    selectedBlueprint: glossaryState.selectedBlueprint,
    parsedSections: glossaryState.parsedSections,
    wizardMode,
  });

  // Code preview generator
  const { codePreview } = useCodePreviewGenerator({
    wizardMode,
    viewDisplayMode,
    initialData,
    gameId,
  });

  // Migration preview data - only fetch when in migration view mode
  const {
    previewData: migrationPreviewData,
    isLoading: migrationPreviewLoading,
    error: migrationPreviewError,
  } = useMigrationPreview(
    viewDisplayMode === "migration" ? initialData?.id : undefined
  );

  // Handler for viewing migration comparison
  const handleViewMigration = useCallback(() => {
    setViewDisplayMode("migration");
  }, []);

  // Event handlers
  const {
    handleEdit,
    handleTitleChange,
    handleRemoveFields,
    handleCreateNewBlueprint,
    handleSave,
    handleDelete,
    handleFormCancel,
    handleRevert,
    proceedWithCancel,
    handleBlueprintChange,
    handleSectionsChange,
    handleViewModeChange,
    handleBackdropClick,
    handleApplyMigration,
    saveHandlerRef,
  } = useObjectWizardHandlers({
    setViewDisplayMode,
    setWizardMode,
    setPendingAction,
    setShowUnsavedDialog,
    setCurrentItemBlueprintVersion,
    setBlueprintChanges,
    glossaryState,
    wizardMode,
    inline,
    initialData,
    gameId,
    glossaryBlueprints,
    blueprintChanges,
    canEdit,
    toast,
    confirmation,
    onClose,
    onSave,
    onUpdate,
    onUnsavedChangesChange,
    unsavedChanges,
    clearDraft,
  });

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Fetch available tags for the current game
  useEffect(() => {
    if (gameId?.id) {
      fetchTags(gameId.id)
        .then((tags) => {
          const tagData = tags.map((tag) => ({ id: tag.id, name: tag.name }));
          glossaryState.updateAvailableTags(tagData);
        })
        .catch(console.error);
    }
  }, [gameId, glossaryState.updateAvailableTags]);

  // Notify parent of unsaved changes state (for tab system integration)
  useEffect(() => {
    if (onUnsavedChangesChange) {
      if (wizardMode === "edit" || wizardMode === "create") {
        // Create stable save handler that calls the latest handleSave
        const stableSaveHandler = async () => {
          if (saveHandlerRef.current) {
            await saveHandlerRef.current();
          }
        };
        // Create discard handler that clears the draft
        const discardHandler = () => {
          clearDraft();
        };
        onUnsavedChangesChange(unsavedChanges.hasChanges, stableSaveHandler, discardHandler);
      } else {
        // When switching to view mode, clear the unsaved changes flag
        // This ensures the tab doesn't show unsaved changes after saving
        onUnsavedChangesChange(false, async () => {}, () => {});
      }
    }
  }, [unsavedChanges.hasChanges, wizardMode, onUnsavedChangesChange, saveHandlerRef, clearDraft]);

  // Check for unsaved changes whenever tracked data changes
  useEffect(() => {
    // Only check for changes after initial state is loaded
    if (hasInitializedRef.current) {
      unsavedChanges.checkForChanges();
    }
  }, [
    glossaryState.itemTitle,
    glossaryState.itemTags,
    glossaryState.itemIdentifiers,
    glossaryState.parsedSections,
    glossaryState.selectedBlueprintId,
    unsavedChanges,
  ]);

  // Save draft data when form state changes (for persistence across tab switches)
  useEffect(() => {
    if ((wizardMode === "edit" || wizardMode === "create") && gameId?.id) {
      // Get current draft data from state
      const currentDraftData = glossaryState.getDraftData();
      // Defer the save to avoid state updates during render
      queueMicrotask(() => {
        saveDraftData(currentDraftData);
      });
    }
  }, [
    glossaryState.itemTitle,
    glossaryState.itemTags,
    glossaryState.itemIdentifiers,
    glossaryState.parsedSections,
    glossaryState.selectedBlueprintId,
    glossaryState.owner,
    glossaryState.editPermissions,
    glossaryState.itemStatus,
    wizardMode,
    gameId?.id,
    saveDraftData,
    glossaryState,
  ]);

  // Restore blueprint object when draft has blueprintId but no blueprint object is set
  useEffect(() => {
    if (hasDraftData && draftData?.blueprintId && !glossaryState.selectedBlueprint && glossaryBlueprints.length > 0) {
      const blueprintFromDraft = glossaryBlueprints.find(bp => bp.id === draftData.blueprintId);
      if (blueprintFromDraft) {
        console.log('[ObjectWizard] Restoring blueprint from draft:', draftData.blueprintId);
        glossaryState.setSelectedBlueprint(blueprintFromDraft);
      }
    }
  }, [hasDraftData, draftData?.blueprintId, glossaryState.selectedBlueprint, glossaryBlueprints, glossaryState]);

  // Update title when component mounts or gameId changes
  useEffect(() => {
    if (gameId?.id && !initialData && !glossaryState.itemTitle) {
      const defaultName = generateDefaultObjectName(gameId.id, glossaryItems);
      glossaryState.setItemTitle(defaultName);
    }
  }, [gameId, glossaryItems, initialData, glossaryState]);

  // Update mode when initialData changes
  useEffect(() => {
    if (initialData) {
      setWizardMode("edit");
    } else {
      setWizardMode("create");
    }
  }, [initialData]);

  // Reset form when modal opens (but NOT if we have draft data to restore)
  useEffect(() => {
    if (isOpen) {
      if (!initialData) {
        // Creating new item - only reset if there's no draft data to restore
        if (!hasDraftData) {
          glossaryState.resetForm();
          if (gameId?.id) {
            const defaultName = generateDefaultObjectName(gameId.id, glossaryItems);
            glossaryState.setItemTitle(defaultName);
          }
        }
        setWizardMode("create");
      } else {
        // Editing existing item
        setWizardMode("edit");
      }
    }
  }, [isOpen, gameId, initialData, glossaryItems, glossaryState, hasDraftData]);

  // ============================================================================
  // RENDER CONDITIONS
  // ============================================================================

  // In modal mode, check isOpen. In inline mode, always render
  if (!inline && !isOpen) return null;

  // ============================================================================
  // RENDER
  // ============================================================================

  // Content that's shared between modal and inline modes
  const wizardContent = (
    <div
      className={
        inline
          ? "relative w-full h-full flex flex-col"
          : "relative bg-card rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col"
      }
    >
      {/* Lock Status Banner */}
      {isLockedByOther && (
        <div className={inline ? "px-6 pt-4" : "px-6 pt-4"}>
          <LockStatusBanner
            lockStatus={lockStatus}
            isAcquiring={isAcquiringLock}
            lockError={lockError}
            canEdit={canEdit}
            onRefresh={refreshLockStatus}
            onForceTakeover={async () => {
              const success = await forceLock();
              if (success) {
                toast({
                  title: t('lock.takeoverSuccess', 'Edit access acquired'),
                  description: t('lock.takeoverSuccessDescription', 'You now have edit access to this item.'),
                });
                setWizardMode('edit' as WizardMode);
              }
            }}
            canForceTakeover={canEditByPermission}
            size="md"
          />
          <UpdateNotificationBanner
            hasPendingUpdate={hasPendingUpdate}
            updatedByName={updatedByName}
            updatedByAvatar={updatedByAvatar}
            updatedAt={updatedAt}
            onRefresh={async () => {
              await onRefreshData?.();
              clearPendingUpdate();
            }}
            onDismiss={clearPendingUpdate}
            size="md"
          />
        </div>
      )}

      {/* Header Section */}
      <div
        className={
          inline
            ? "flex-shrink-0 pt-6 border-b border-grey-G2 bg-background"
            : "flex-shrink-0 pt-6 px-6 border-b border-grey-G2"
        }
      >
        <div className={inline ? "max-w-4xl mx-auto px-6 pb-6" : ""}>
          <UnifiedWizardHeader
            title={glossaryState.itemTitle}
            mode={wizardMode}
            itemType="library"
            onTitleChange={handleTitleChange}
            onCancel={handleFormCancel}
            onRevert={initialData?.id ? handleRevert : undefined}
            onSubmit={handleSave}
            isSubmitting={glossaryState.isSaving}
            hasUnsavedChanges={unsavedChanges.hasChanges}
            additionalBadges={<DraftBadge status={initialData?.status} />}
            submitDisabled={false}
            submitText="Create Library Object"
            itemId={initialData?.id}
            onEdit={handleEdit}
            onDelete={handleDelete}
            blueprint={glossaryState.selectedBlueprint}
            version={initialData?.version}
            tags={glossaryState.itemTags}
            tagOptions={glossaryState.availableTags.map((tag) => ({
              id: tag.id,
              label: tag.name,
            }))}
            identifiers={glossaryState.itemIdentifiers}
            owner={glossaryState.owner}
            editPermissions={glossaryState.editPermissions}
            canEdit={canEdit}
            status={glossaryState.itemStatus}
            onStatusChange={glossaryState.setItemStatus}
          />
        </div>
      </div>

      {/* Main Content Section */}
      <div
        className={
          inline
            ? "flex-1 overflow-y-auto bg-background"
            : "flex-1 pt-6 px-6 overflow-y-auto"
        }
      >
        <ObjectWizardContent
          inline={inline}
          wizardMode={wizardMode}
          viewDisplayMode={viewDisplayMode}
          availableViewModes={availableViewModes}
          initialData={initialData}
          glossaryBlueprints={glossaryBlueprints}
          isLoading={isLoading}
          gameId={gameId}
          codePreview={codePreview}
          blueprintChanges={blueprintChanges}
          migrationPreviewData={migrationPreviewData}
          migrationPreviewLoading={migrationPreviewLoading}
          migrationPreviewError={migrationPreviewError}
          glossaryState={glossaryState}
          canEdit={canEdit}
          onBlueprintChange={handleBlueprintChange}
          onSectionsChange={handleSectionsChange}
          onFormCancel={handleFormCancel}
          onViewModeChange={handleViewModeChange}
          onViewMigration={handleViewMigration}
          onApplyMigration={handleApplyMigration}
        />
      </div>
    </div>
  );

  // Return modal or inline version
  if (inline) {
    return (
      <>
        {wizardContent}
        {/* Unsaved Changes Dialog */}
        <UnsavedChangesDialog
          isOpen={showUnsavedDialog}
          onClose={() => {
            setShowUnsavedDialog(false);
            setPendingAction(null);
          }}
          onSave={async () => {
            await handleSave();
            setShowUnsavedDialog(false);
            setPendingAction(null);
          }}
          onDiscard={() => {
            unsavedChanges.reset();
            proceedWithCancel();
            setShowUnsavedDialog(false);
            setPendingAction(null);
          }}
          title={t("platform.wizards.unsaved_data")}
          description="You have unsaved changes to this library object. Would you like to save them before closing?"
        />
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex justify-center items-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
        />

        <div className="relative z-10 w-full max-w-7xl h-[90vh]">
          {wizardContent}
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={() => {
          setShowUnsavedDialog(false);
          setPendingAction(null);
        }}
        onSave={async () => {
          await handleSave();
          setShowUnsavedDialog(false);
          setPendingAction(null);
        }}
        onDiscard={() => {
          unsavedChanges.reset();
          proceedWithCancel();
          setShowUnsavedDialog(false);
          setPendingAction(null);
        }}
        title={t("platform.wizards.glossary.unsaved_object_changes")}
        description="You have unsaved changes to this library object. Would you like to save them before closing?"
      />
    </>
  );
};

